import React, { useMemo } from 'react';
import cn from 'clsx';
import queryString from 'query-string';

import Button from '../../library/button/button';
import Image from '../../library/image/image';

import { useMessage } from '../../shared/hooks/hooks';

import { getAffiliateMarketingParams } from '../../shared/utils/url-params/url-params';

import { DT_GEOLOCATING_STORE_SEARCH_EXPANDED_RADIUS } from '../../shared/constants/messages';
import { SEARCH_RADIUS_OPTIONS } from '../../shared/constants/search-types';

import styles from './store-locator-message.scss';

interface StoreLocatorMessageProps {
  className?: string;
  isValidSearch?: boolean;
  latestSearch?: string;
  onShopTireRackClick?: () => void;
  searchRadius?: (typeof SEARCH_RADIUS_OPTIONS)[number]['value'];
}

function StoreLocatorMessage(props: StoreLocatorMessageProps) {
  const {
    className = '',
    isValidSearch,
    latestSearch,
    onShopTireRackClick,
    searchRadius = SEARCH_RADIUS_OPTIONS[SEARCH_RADIUS_OPTIONS.length - 1].value
  } = props;
  const affiliateMarketingUrlParams = queryString.stringify(
    getAffiliateMarketingParams()
  );

  let urlParams = affiliateMarketingUrlParams
    ? `?${affiliateMarketingUrlParams}`
    : '';

  const messages = useMessage([DT_GEOLOCATING_STORE_SEARCH_EXPANDED_RADIUS]);
  const nextSearchRadius = useMemo(
    () =>
      SEARCH_RADIUS_OPTIONS.find(option => option.value > searchRadius)?.value,
    [searchRadius]
  );

  return (
    <div className={cn(styles['message-container'], className)}>
      {!nextSearchRadius && (
        <Image
          alt="Tire Rack Logo"
          className={styles['logo']}
          loading="lazy"
          src="https://www.tirerack.com/content/dam/tirerack/desktop/Navigation/TireRackLogo.png"
        />
      )}

      <div className={styles['content']}>
        {!nextSearchRadius && (
          <>
            {isValidSearch ? (
              <p>
                The nearest store to &quot;{latestSearch}&quot; is more
                than&nbsp;
                <strong>
                  {messages?.[DT_GEOLOCATING_STORE_SEARCH_EXPANDED_RADIUS] ||
                    '150'}
                  &nbsp;miles
                </strong>
                &nbsp;away.
              </p>
            ) : (
              <p>There are no stores to show based on your query.</p>
            )}
            <p>
              Head to Tire Rack to shop for tires, wheels, and accessories
              online with local installation options across the nation.
            </p>
            <p>
              Since 2022, Tire Rack has been a part of the Discount Tire family.
              This combines the best of both worlds; Tire Rack&apos;s
              first-class online shopping experience and Discount Tire&apos;s
              first-class in-store service.
            </p>
          </>
        )}

        {nextSearchRadius && (
          <p>
            The nearest store to &quot;{latestSearch}&quot; is more than&nbsp;
            <strong>{searchRadius} miles </strong>
            away. Try expanding your search to {nextSearchRadius} miles.{' '}
          </p>
        )}
      </div>
      <Button
        className={styles['shop-button']}
        href={`${process.env.__TIRE_RACK_HOST__}${urlParams}`}
        isFullWidth
        isPrimary
        onClick={() => onShopTireRackClick?.()}
        target="_blank"
      >
        Shop Tire Rack
      </Button>
    </div>
  );
}

export default StoreLocatorMessage;
